/**
 * The draggable markers on the horizontal ruler: first line, left indent, right indent, tab stops.
 *
 * WHAT THEY ARE FOR. The ruler was a measure and nothing more -- it told an author how wide the
 * text column was and left them to reach for a toolbar button to do anything about it. These are
 * the controls a word processor puts in exactly this place, and the two that have no toolbar
 * equivalent at all are the ones worth having: a HANGING indent, where the first line starts left
 * of the rest, which is how a numbered clause in a procedure is written; and a RIGHT indent, which
 * is half of what makes a quotation look like a quotation.
 *
 * THEY ACT ON THE BLOCK THE CARET IS IN, and on every block in a selection that spans several.
 * The positions they show are read from the first of them, which is what a word processor does and
 * is the only answer that is not a lie when they differ.
 *
 * MEASURED FROM THE TEXT MARGIN, NOT THE SHEET EDGE. "Indent it half an inch" means half an inch
 * into the text, which is what the ruler's own numbering already says.
 *
 * MARGINS ARE NOT TOUCHED. Dragging these changes a PARAGRAPH. The page's margins belong to the
 * rendition and the rendition belongs to the template -- a marker that silently moved them would
 * make two revisions of one procedure print differently with nothing in the record saying why.
 * That reasoning is the ruler's own and has not changed; what changed is that paragraph indents
 * were never part of it.
 */
import React from "react";
import { Box } from "@mui/material";

import { blockIndents, setBlockIndent, setTabStops, tabStopsOf, MAX_INDENT } from "./Formatting";

const Blue = "#1A73E8";

/** Drag resolution. An eighth of an inch is what the ruler's own ticks are drawn at. */
const SNAP = 1 / 8;

type Which = "firstLine" | "indent" | "indentRight";

/**
 * One drag, in inches along the ruler.
 *
 * POINTER CAPTURE, so the drag survives the pointer leaving a marker eight pixels wide -- without
 * it the handle is lost the moment somebody moves faster than carefully, which is most people.
 */
function useDrag(onMove: (inches: number) => void, onDone: () => void) {
    return React.useCallback((event: React.PointerEvent, pxPerInch: number, sign: 1 | -1,
                             startInches: number) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const target = event.currentTarget as HTMLElement;
        target.setPointerCapture(event.pointerId);

        const move = (e: PointerEvent) => {
            // THE SIGN IS THE RIGHT MARKER'S WHOLE PROBLEM. Its value is measured INWARD from the
            // right edge of the measure, so dragging it left -- towards the middle of the page --
            // must make the number go UP. Without this it wrote a negative right indent, which CSS
            // happily accepts and which pushes the paragraph out past the margin.
            const delta = sign * (e.clientX - startX) / pxPerInch;
            onMove(Math.max(0, Math.round((startInches + delta) / SNAP) * SNAP));
        };
        const up = (e: PointerEvent) => {
            try { target.releasePointerCapture(e.pointerId); } catch { /* already released */ }
            target.removeEventListener("pointermove", move);
            target.removeEventListener("pointerup", up);
            target.removeEventListener("pointercancel", up);
            onDone();
        };
        target.addEventListener("pointermove", move);
        target.addEventListener("pointerup", up);
        target.addEventListener("pointercancel", up);
    }, [onMove, onDone]);
}

export default function RulerMarkers({ editor, editable, marginInches, measureInches, editorState }: {
    editor: any;
    editable?: boolean;
    /** The page margin, in inches: where the text column starts. */
    marginInches: number;
    /** The width of the text column, in inches. */
    measureInches: number;
    /** Bumped on every transaction, so the markers follow the caret. */
    editorState?: number;
}) {
    const host = React.useRef<HTMLDivElement | null>(null);
    // WHAT IS BEING DRAGGED RIGHT NOW, so the marker can follow the pointer before the document has
    // been told. Writing on every pointermove would put a hundred steps in the undo history.
    const [dragging, setDragging] = React.useState<{which: Which; inches: number} | null>(null);

    const stored = editor ? blockIndents(editor) : {indent: 0, firstLine: 0, indentRight: 0};
    const stops: number[] = editor ? tabStopsOf(editor) : [];
    void editorState;

    const pxPerInch = () => {
        const box = host.current?.getBoundingClientRect();
        return box ? box.width / (measureInches + 2 * marginInches) : 96;
    };

    const live = (which: Which) =>
        (dragging && dragging.which === which ? dragging.inches : (stored as any)[which]) as number;

    const commit = (which: Which, inches: number) => {
        setBlockIndent(editor, which, inches);
        setDragging(null);
    };

    // A DRAG ENDS IN A CLICK, and the click lands on the ruler behind the marker -- which was
    // adding a tab stop every single time anybody moved an indent. Three drags, three stops nobody
    // asked for. The click is suppressed for a moment after a drag rather than by fighting the
    // event order, because the thing being distinguished is intent and the only signal for it is
    // whether a drag just happened.
    const draggedAt = React.useRef(0);

    const beginDrag = useDrag(
        (inches) => setDragging((d) => (d ? {...d, inches} : d)),
        () => setDragging((d) => { draggedAt.current = Date.now(); if (d) commit(d.which, d.inches); return null; }),
    );

    /** Where a marker sits on the ruler, in inches from the sheet's left edge. */
    const atLeft = (inches: number) => marginInches + Math.max(0, Math.min(MAX_INDENT, inches));
    const atRight = (inches: number) =>
        marginInches + measureInches - Math.max(0, Math.min(MAX_INDENT, inches));

    const start = (which: Which) => (event: React.PointerEvent) => {
        if (!editable) return;
        setDragging({which, inches: live(which)});
        beginDrag(event, pxPerInch(), which === "indentRight" ? -1 : 1, live(which));
    };

    /**
     * A CLICK ON THE RULER ADDS A TAB STOP, which is how every word processor has done it for
     * thirty years and the only discoverable way there is. Clicking an existing one removes it --
     * Google drags it off the ruler instead, which is harder to do by accident and harder to find.
     */
    const addStop = (event: React.MouseEvent) => {
        if (!editable || !editor) return;
        // Not the click that ends a drag, and not a click ON a marker.
        if (Date.now() - draggedAt.current < 400) return;
        if (event.target !== event.currentTarget) return;
        const box = host.current?.getBoundingClientRect();
        if (!box) return;
        const inches = (event.clientX - box.left) / pxPerInch() - marginInches;
        if (inches <= 0 || inches >= measureInches) return;           // not in the text column
        const at = Math.round(inches / SNAP) * SNAP;
        const near = stops.find((s) => Math.abs(s - at) < SNAP);
        setTabStops(editor, near !== undefined ? stops.filter((s) => s !== near)
                                               : [...stops, at].sort((a, b) => a - b));
    };

    const grab = editable ? ("ew-resize" as const) : ("default" as const);

    return (
        <Box ref={host} onClick={addStop} sx={{
            position: "absolute", inset: 0,
            // THE RULER UNDERNEATH STAYS CLICKABLE: this layer is the markers and the empty space
            // between them, and the empty space is where a tab stop is added.
            cursor: editable ? "copy" : "default",
        }}>
            {/* THE FIRST-LINE MARKER: a triangle pointing DOWN, at the top. Google's shape, and the
                shape is the affordance -- down for the line that starts, up for the block beneath. */}
            <Box onPointerDown={start("firstLine")} title="First line indent" sx={{
                position: "absolute", top: 0, left: `${atLeft(stored.indent + live("firstLine"))}in`,
                transform: "translateX(-50%)", width: 0, height: 0, cursor: grab,
                borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
                borderTop: `7px solid ${Blue}`, zIndex: 3,
            }}/>

            {/* THE LEFT INDENT: a rectangle below it, which moves the whole paragraph. Dragging it
                carries the first-line marker along, because the first line is measured FROM the
                left indent -- that is what makes a hanging indent stay hanging. */}
            <Box onPointerDown={start("indent")} title="Left indent" sx={{
                position: "absolute", bottom: 3, left: `${atLeft(live("indent"))}in`,
                transform: "translateX(-50%)", width: 9, height: 7, cursor: grab,
                backgroundColor: Blue, borderRadius: "1px", zIndex: 3,
            }}/>

            {/* THE RIGHT INDENT: a triangle pointing UP at the right end of the measure. */}
            <Box onPointerDown={start("indentRight")} title="Right indent" sx={{
                position: "absolute", bottom: 0, left: `${atRight(live("indentRight"))}in`,
                transform: "translateX(-50%)", width: 0, height: 0, cursor: grab,
                borderLeft: "5px solid transparent", borderRight: "5px solid transparent",
                borderBottom: `7px solid ${Blue}`, zIndex: 3,
            }}/>

            {/* TAB STOPS, as Google draws them: a small mark sitting on the ruler's baseline.
                EACH ONE REMOVES ITSELF, and it has to say so here rather than relying on the
                layer's click: that handler ignores anything whose target is not the layer ITSELF,
                which is every one of these markers. The tooltip has said "click to remove" since
                the day they were drawn, and clicking one did nothing at all -- what removed a stop
                was clicking NEAR it and missing, because an eighth-inch tolerance made a miss read
                as a hit on the stop. Aim well and nothing happened; aim badly and it worked. */}
            {stops.map((inches) => (
                <Box key={inches} title={`Tab stop at ${inches}"  (click to remove)`}
                     onClick={(event) => {
                         event.stopPropagation();
                         if (!editable || !editor) return;
                         setTabStops(editor, stops.filter((s) => s !== inches));
                     }}
                     sx={{
                    position: "absolute", bottom: 0, left: `${marginInches + inches}in`,
                    transform: "translateX(-50%)", width: 0, height: 0,
                    borderLeft: "4px solid transparent", borderRight: "4px solid transparent",
                    borderBottom: `6px solid ${Blue}`, opacity: 0.85, zIndex: 2,
                    cursor: editable ? "pointer" : "default",
                }}/>
            ))}
        </Box>
    );
}
